/**
 * StudentSurveyForm — reusable form component for both uni-wide and
 * course-specific surveys. Renders a 1-5 star Likert grid + free-text
 * boxes + PII fields + a follow-up consent checkbox. Submits to
 * /api/surveys/submit, which lands the row in status="pending".
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, Star } from "lucide-react";

const YEAR_OPTIONS = ["Year 1", "Year 2", "Year 3", "Year 4", "Postgraduate", "Alumni"];

const UNI_RATING_FIELDS = [
  ["teaching_quality",      "Teaching quality"],
  ["value_for_money",       "Value for money"],
  ["campus_and_facilities", "Campus and facilities"],
  ["student_support",       "Wellbeing and student support"],
  ["career_prep",           "Careers support and employer engagement"],
  ["social_life",           "Social life and community"],
  ["accommodation",         "Accommodation"],
];

const COURSE_RATING_FIELDS = [
  ["content_quality",       "Content quality and relevance"],
  ["teaching_engagement",   "Teaching engagement"],
  ["assessment_fairness",   "Assessment fairness and feedback"],
  ["workload_manageable",   "Workload is manageable"],
  ["career_relevance",      "Career preparation"],
  ["value_for_money",       "Value for money"],
];

export default function StudentSurveyForm({
  universityId,
  degreeId = null,
  courseTitle = null,  // optional prefill
  onSuccess = () => {},
}) {
  const isCourse = !!degreeId;
  const fields = isCourse ? COURSE_RATING_FIELDS : UNI_RATING_FIELDS;
  const [state, setState] = useState({
    student_name: "",
    student_email: "",
    course_name: courseTitle || "",
    year_of_study: "",
    follow_up_ok: false,
    most_positive: "",
    most_negative: "",
    advice_to_applicants: "",
    would_recommend: "",
  });
  const [ratings, setRatings] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const setField = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const setRating = (k, v) => setRatings((r) => ({ ...r, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!state.student_name || !state.student_email || !state.course_name || !state.year_of_study) {
      setError("Please fill in your name, email, course and year of study.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/surveys/submit", {
        university_id: universityId,
        degree_id: degreeId,
        ...state,
        ...ratings,
      });
      setSubmitted(true);
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="survey-form-success"
        className="rounded-md border border-emerald-300 bg-emerald-50 p-5"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-emerald-900">Thanks — response received.</p>
            <p className="mt-1 text-sm text-emerald-900/80">
              Your response is queued for moderation. We publish
              aggregated ratings on this page once ≥5 approved responses
              exist. Nothing will be attributed to your name publicly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="student-survey-form">
      {/* PII block */}
      <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Your name" required>
          <input type="text" value={state.student_name}
                 onChange={(e) => setField("student_name", e.target.value)}
                 data-testid="survey-input-name"
                 className="wua-input" placeholder="Jane Chen" />
        </FormField>
        <FormField label="Your email" required
                   hint="We never publish this. Verified so responses count.">
          <input type="email" value={state.student_email}
                 onChange={(e) => setField("student_email", e.target.value)}
                 data-testid="survey-input-email"
                 className="wua-input" placeholder="jane@uni.ac.uk" />
        </FormField>
        <FormField label="Course you studied / study" required>
          <input type="text" value={state.course_name}
                 onChange={(e) => setField("course_name", e.target.value)}
                 data-testid="survey-input-course"
                 className="wua-input" placeholder="BSc Economics" />
        </FormField>
        <FormField label="Year of study" required>
          <select value={state.year_of_study}
                  onChange={(e) => setField("year_of_study", e.target.value)}
                  data-testid="survey-input-year"
                  className="wua-input">
            <option value="">Select year…</option>
            {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </FormField>
      </fieldset>

      {/* Ratings block */}
      <fieldset>
        <legend className="label-uppercase mb-3">
          On a scale of 0-10, how likely are you to recommend {isCourse ? "this course" : "this university"}?
        </legend>
        <NpsRow
          value={ratings.overall_satisfaction}
          onChange={(v) => setRating("overall_satisfaction", v)}
          isCourse={isCourse}
        />
      </fieldset>

      <fieldset>
        <legend className="label-uppercase mb-3">Rate each on a 1-5 scale</legend>
        <div className="space-y-3">
          {fields.map(([k, label]) => (
            <RatingRow key={k} label={label} value={ratings[k]} onChange={(v) => setRating(k, v)} />
          ))}
        </div>
      </fieldset>

      {/* Would recommend */}
      <fieldset>
        <legend className="label-uppercase mb-3">
          Would you recommend {isCourse ? "this course" : "this university"} to a friend?
        </legend>
        <div className="flex gap-2" data-testid="survey-recommend">
          {[["yes", "Yes"], ["maybe", "Maybe"], ["no", "No"]].map(([v, l]) => (
            <button key={v} type="button"
                    onClick={() => setField("would_recommend", v)}
                    data-testid={`survey-recommend-${v}`}
                    className={
                      "px-4 py-1.5 rounded-full border text-sm transition-colors " +
                      (state.would_recommend === v
                        ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                        : "bg-white text-stone-700 border-stone-300 hover:border-[#4C1D95]")
                    }>
              {l}
            </button>
          ))}
        </div>
      </fieldset>

      {/* Free text */}
      <FormField label="What worked well?">
        <textarea rows={3} value={state.most_positive}
                  onChange={(e) => setField("most_positive", e.target.value)}
                  data-testid="survey-input-positive"
                  className="wua-input resize-y"
                  placeholder="The stats faculty were world-class and available for office hours…" />
      </FormField>
      <FormField label="What didn't work?">
        <textarea rows={3} value={state.most_negative}
                  onChange={(e) => setField("most_negative", e.target.value)}
                  data-testid="survey-input-negative"
                  className="wua-input resize-y"
                  placeholder="Careers support was slow to respond during application season…" />
      </FormField>
      <FormField label="Advice for future applicants">
        <textarea rows={2} value={state.advice_to_applicants}
                  onChange={(e) => setField("advice_to_applicants", e.target.value)}
                  data-testid="survey-input-advice"
                  className="wua-input resize-y"
                  placeholder="Apply early for accommodation. Take the second-year quant module…" />
      </FormField>

      {/* Consent */}
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" checked={state.follow_up_ok}
               onChange={(e) => setField("follow_up_ok", e.target.checked)}
               data-testid="survey-input-follow-up"
               className="mt-1" />
        <span className="text-sm text-stone-700">
          It's OK to email me for follow-up research. WUA may contact
          me to expand on my response or run a short interview. I can
          opt out any time.
        </span>
      </label>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="survey-form-error">
          {error}
        </div>
      )}

      <div className="pt-2">
        <button type="submit" disabled={submitting}
                data-testid="survey-submit-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#4C1D95] text-white text-sm font-semibold hover:bg-[#3B1782] disabled:opacity-50">
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Submit response
        </button>
        <p className="mt-2 text-[11px] text-stone-500 font-mono">
          Response goes into moderation. Nothing is attributed to your
          name publicly.
        </p>
      </div>
    </form>
  );
}


function FormField({ label, required = false, hint = null, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider font-mono text-stone-500 mb-1">
        {label} {required && <span className="text-[#4C1D95]">*</span>}
      </div>
      {children}
      {hint && <div className="mt-1 text-[11px] text-stone-500">{hint}</div>}
    </label>
  );
}


function RatingRow({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-stone-100 last:border-0">
      <div className="text-sm text-stone-800 flex-1 min-w-0">{label}</div>
      <div className="flex gap-1 shrink-0" role="radiogroup" aria-label={label}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button"
                  aria-checked={value === n}
                  role="radio"
                  onClick={() => onChange(n === value ? null : n)}
                  data-testid={`rating-${label.slice(0, 20).replace(/[^a-z0-9]/gi, "-")}-${n}`}
                  className="p-1 rounded hover:bg-stone-100 transition-colors">
            <Star className={
              "h-5 w-5 transition-colors " +
              (value != null && n <= value
                ? "fill-amber-400 stroke-amber-400"
                : "stroke-stone-300")
            } />
          </button>
        ))}
      </div>
    </div>
  );
}


/**
 * NpsRow — 0-10 button strip that doubles as an NPS score.
 * Promoters 9-10 (emerald), Passives 7-8 (amber), Detractors 0-6 (red).
 */
function NpsRow({ value, onChange, isCourse }) {
  const shade = (n) => {
    const selected = value === n;
    if (n <= 6) return selected ? "bg-red-600 text-white border-red-600" : "border-red-200 text-red-800 hover:border-red-500";
    if (n <= 8) return selected ? "bg-amber-500 text-white border-amber-500" : "border-amber-200 text-amber-800 hover:border-amber-500";
    return selected ? "bg-emerald-600 text-white border-emerald-600" : "border-emerald-200 text-emerald-800 hover:border-emerald-500";
  };
  return (
    <div data-testid="survey-nps-row">
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Likelihood to recommend, 0-10">
        {[0,1,2,3,4,5,6,7,8,9,10].map((n) => (
          <button key={n} type="button"
                  onClick={() => onChange(n === value ? null : n)}
                  data-testid={`survey-nps-${n}`}
                  aria-checked={value === n}
                  role="radio"
                  className={
                    "min-w-[38px] px-2 py-1.5 rounded border font-mono text-sm transition-colors " +
                    shade(n)
                  }>
            {n}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-[10px] font-underground-sc tracking-wider text-stone-500 mt-2">
        <span>Not at all likely</span>
        <span>Extremely likely</span>
      </div>
      <p className="mt-2 text-[11px] text-stone-500">
        {isCourse ? "This course-level" : "This university-level"} score feeds our Net Promoter benchmark — 9-10 = Promoter, 7-8 = Passive, 0-6 = Detractor.
      </p>
    </div>
  );
}
